import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'
import { Controller } from 'react-hook-form'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import FullScreenPickerSheet from '../../FullScreenPickerSheet/FullScreenPickerSheet'
import StruxhubUserSelector from '../../StruxhubInputs/StruxhubSelector/StruxhubUserSelector'
import UserListSelector, {
  GroupingOption,
} from '../../UserListSelector/UserListSelector'
import { useUserListSelectorData } from '../../UserListSelector/useUserListSelectorData'
import { UserFieldConfig } from '../types'

interface UserFieldProps {
  field: UserFieldConfig
  control: any
  error?: string
  hideHelperText?: boolean
}

interface UserPickerBodyProps {
  label: string
  value: string | null | undefined
  onChange: (value: string | null) => void
  onClose: () => void
}

// Extracted so the heavy selector-data hook runs only when the picker opens,
// not on every form render. `observer` keeps those reads reactive.
const UserPickerBody = observer(function UserPickerBody({
  label,
  value,
  onChange,
  onClose,
}: UserPickerBodyProps) {
  const stores = React.useContext(MobXProviderContext) as any
  const { users, userMetadata, currentUser, filterData } =
    useUserListSelectorData({
      projectMembersStore: stores.projectMembersStore,
      userProjectsStore: stores.userProjectsStore,
      companiesStore: stores.companiesStore,
      state: stores.state,
      projectRolesStore: stores.projectRolesStore,
      projectTeamsStore: stores.projectTeamsStore,
      projectTradesStore: stores.projectTradesStore,
      tagsStore: stores.tagsStore,
    })

  return (
    <UserListSelector
      title={Localization.translator.selectX(label)}
      selectedUserIds={new Set(value ? [value] : [])}
      onUserToggle={userId => onChange(userId === value ? null : userId)}
      users={users}
      userMetadata={userMetadata}
      currentUser={currentUser}
      filterData={filterData}
      selectionMode="single"
      onClose={onClose}
      className="tw:flex-1"
      defaultGrouping={GroupingOption.COMPANY}
    />
  )
})

export default function UserField({
  field,
  control,
  error,
  hideHelperText,
}: UserFieldProps) {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)

  const closePicker = () => setIsPickerOpen(false)

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={field.defaultValue}
      rules={{
        required: field.required
          ? `${field.label || field.name} is required`
          : false,
      }}
      render={({ field: { onChange, value } }) => (
        <>
          <StruxhubUserSelector
            label={field.label}
            value={value}
            isRequired={field.required}
            isReadOnly={field.readOnly || field.displayOnly}
            isDisabled={field.disabled}
            isValid={!error}
            validationMessage={error}
            isHelperTextHidden={hideHelperText}
            onClick={() => setIsPickerOpen(true)}
            onValueReset={() => onChange(undefined)}
          />
          <FullScreenPickerSheet isOpen={isPickerOpen} onClose={closePicker}>
            <UserPickerBody
              label={field.label || field.name}
              value={value}
              onChange={onChange}
              onClose={closePicker}
            />
          </FullScreenPickerSheet>
        </>
      )}
    />
  )
}
